import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import config from "./config.js";
import { pinPromptWithOptions } from "./utils/pinPrompt";
import {
  ADMIN_REMEMBER_DAYS,
  canRememberAdmin,
  clearAdminSessionKeys,
  forgetRememberedAdmin,
  hasRememberedAdmin,
  rememberAdminDevice,
  rememberedAdminExpiresAt,
  setAdminSessionKeys,
  unlockRememberedAdmin,
} from "./utils/adminRemember";
import { isPhoneAppNow } from "./mobile/mobileMode";
import { phoneAlert } from "./mobile/ui/dialogs";

// The admin unlock (moved out of App.jsx 2026-10-08 so the phone shell can use it too).
// Admin access is client-side: an Admin PIN checked by POST /auth/pin, kept in sessionStorage
// (adminVerified, userId, userName, userRole, adminPin) for this tab. On a phone the unlock is
// remembered for 30 days through a key JobQ holds (utils/adminRemember.js, since 2026-10-20).

export const AdminContext = createContext(null);

const PIN_ENDPOINT = `${config.API_BASE_URL}/auth/pin`;
const API_BASE = config.API_BASE_URL;

export function isAdminSessionActive() {
  const role = sessionStorage.getItem("userRole") || "";
  return role.toLowerCase() === "admin";
}

function adminFromSession() {
  if (!isAdminSessionActive()) return null;
  return {
    userId: sessionStorage.getItem("userId"),
    userName: sessionStorage.getItem("userName"),
    role: sessionStorage.getItem("userRole"),
  };
}

const REMEMBER_FAILED = {
  no_crypto: "This browser can't keep the unlock safely: it lasts until the app is closed.",
  not_configured: "JobQ can't remember phones yet: the unlock lasts until the app is closed.",
  offline: "Couldn't reach JobQ to remember this phone: the unlock lasts until the app is closed.",
};

export function AdminProvider({ children }) {
  // Read once, before the first render: a ProtectedRoute's effect runs before this provider's
  // effects, so reading in an effect asked an unlocked user for the PIN again on every reload.
  const [adminData, setAdminData] = useState(() => adminFromSession());
  // A remembered unlock is being asked of JobQ (app start, or the app came back after an offline
  // start): ProtectedRoute waits for it instead of asking for the PIN.
  const [restoring, setRestoring] = useState(() => !isAdminSessionActive() && hasRememberedAdmin());
  const [rememberedUntil, setRememberedUntil] = useState(() => rememberedAdminExpiresAt());
  // What to tell the person: why this phone wasn't remembered, or why JobQ no longer remembers it
  // ("Your Admin PIN changed…"). Shown in the More panel and on the next PIN prompt.
  const [rememberNote, setRememberNote] = useState(null);

  useEffect(() => {
    if (!restoring) return undefined;
    let live = true;
    unlockRememberedAdmin({ apiBase: API_BASE }).then((res) => {
      if (!live) return;
      if (isAdminSessionActive()) {
        // unlocked by a typed PIN while this was on its way: that unlock stands
      } else if (res.user && res.user.pin) {
        setAdminSessionKeys(res.user);
        setAdminData({ userId: res.user.userId, userName: res.user.userName, role: String(res.user.role || "admin").toLowerCase() });
        setRememberNote(null);
      } else if (res.locked && res.reason !== "none") {
        setRememberNote(res.message || "This phone no longer remembers the admin unlock.");
      }
      setRememberedUntil(rememberedAdminExpiresAt());
      setRestoring(false);
    });
    return () => { live = false; };
  }, [restoring]);

  // No answer at start-up (offline, JobQ down): try again when the app is back online or in front.
  useEffect(() => {
    const retry = () => {
      if (document.visibilityState === "hidden") return;
      if (!isAdminSessionActive() && hasRememberedAdmin()) setRestoring(true);
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", retry);
    return () => {
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", retry);
    };
  }, []);

  // remember: true = ticked (remember this phone), false = unticked on purpose (forget it), null = the prompt
  // offered no box (desktop layout, a wide phone): leave any remembered unlock as it is.
  const loginAsAdmin = useCallback((user, { remember = null } = {}) => {
    setAdminSessionKeys(user);
    setRememberNote(null);
    if (remember === true) {
      rememberAdminDevice(user, { apiBase: API_BASE }).then((r) => {
        // an older record (if any) is still there and still unlocks next time
        setRememberedUntil(r.ok ? r.expiresAt : rememberedAdminExpiresAt());
        if (!r.ok) {
          setRememberNote(REMEMBER_FAILED[r.reason] || r.message || "This phone wasn't remembered: the unlock lasts until the app is closed.");
        }
      });
    } else if (remember === false && hasRememberedAdmin()) {
      // unticked on purpose: this phone stops remembering (and is signed out at JobQ)
      forgetRememberedAdmin({ apiBase: API_BASE });
      setRememberedUntil(null);
    }

    setAdminData({
      userId: user.userId,
      userName: user.userName,
      role: user.role.toLowerCase()
    });
  }, []);

  // "Clear Credentials": ends the tab's unlock AND a remembered one (signed out at JobQ too).
  const logoutAdmin = useCallback(() => {
    clearAdminSessionKeys();
    forgetRememberedAdmin({ apiBase: API_BASE });
    setRememberedUntil(null);
    setRememberNote(null);
    setRestoring(false);
    setAdminData(null);
  }, []);

  // A remembered unlock ends at its expiry even if the app stays open. Timers stall while an
  // iPhone app is in the background, so it is checked again whenever the app comes back. A timer
  // can't wait the whole 30 days (setTimeout tops out at ~24.8 days): it re-arms until then.
  const [expiryTick, setExpiryTick] = useState(0);
  useEffect(() => {
    if (!rememberedUntil) return undefined;
    const check = () => {
      if (Date.now() >= rememberedUntil) logoutAdmin();
    };
    const ms = Math.min(Math.max(rememberedUntil - Date.now(), 0), 2147483000);
    const t = setTimeout(() => {
      if (Date.now() >= rememberedUntil) logoutAdmin();
      else setExpiryTick((n) => n + 1);
    }, ms);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", check);
    };
  }, [rememberedUntil, logoutAdmin, expiryTick]);

  const isAdminActive = Boolean(adminData && adminData.role === "admin");
  const value = useMemo(
    () => ({ adminData, isAdminActive, loginAsAdmin, logoutAdmin, rememberedUntil, restoring, rememberNote }),
    [adminData, isAdminActive, loginAsAdmin, logoutAdmin, rememberedUntil, restoring, rememberNote]
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

function say(message) {
  if (isPhoneAppNow()) return phoneAlert(message, { title: "Admin PIN" });
  window.alert(message);
  return Promise.resolve();
}

/**
 * Ask for an Admin PIN and check it. Resolves {userId, userName, role, pin, remember} or null.
 * On a phone the prompt offers "Remember on this phone for 30 days", ticked; remember is then
 * true / false, and null when no box was offered. `reason` (why JobQ no longer remembers this
 * phone) goes above the question.
 */
export async function requestAdminPin({ reason = null } = {}) {
  const offerRemember = isPhoneAppNow() && canRememberAdmin();
  // face: false: an admin unlock keeps its PIN for the whole session (sessionStorage adminPin,
  // X-Admin-Pin), and a face token must never outlive the action it was made for.
  // autofill: false: this IS the unlock. A session PIN JobQ just refused (changed since) must be
  // typed again, never filled back in (it used to loop on "Invalid PIN").
  const answer = await pinPromptWithOptions(reason ? `${reason} Enter your Admin PIN:` : "Enter your Admin PIN:", {
    autofill: false,
    face: false,
    checkbox: offerRemember
      ? {
        label: `Remember on this phone for ${ADMIN_REMEMBER_DAYS} days`,
        help: "Untick on a phone that isn't yours. Clear Credentials, a new PIN, or Sign out in User Management ends it sooner.",
        defaultChecked: true,
      }
      : null,
  });
  if (!answer) return null;
  const { pin, checked } = answer;

  try {
    const res = await fetch(PIN_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pin })
    });

    if (!res.ok) {
      if (res.status === 401) {
        await say("Invalid PIN or you are not an admin.");
      } else {
        await say(`Error verifying PIN: ${res.status} ${res.statusText}`);
      }
      return null;
    }

    const data = await res.json();
    if (data.role && data.role.toLowerCase() === "admin") {
      return {
        userId: data.user_id,
        userName: data.name,
        role: data.role,
        pin: pin,
        remember: offerRemember ? Boolean(checked) : null,
      };
    } else {
      await say("You do not have an admin role.");
      return null;
    }
  } catch (err) {
    await say("Error verifying PIN: " + err.message);
    return null;
  }
}
